import { type FunctionComponent, useCallback, useMemo } from "react";
import classNames from "classnames";
import UpsellSuggestions from "@ppb/tbd-components-flexible-betting-opportunities/components/UpsellSuggestions/view/UpsellSuggestions.web";
import { SportsbookReceiptPanel as BetslipSportsbookReceiptPanel } from "./snowflakes/SportsbookReceiptPanel/SportsbookReceiptPanel.web";
import styles from "./SportsbookReceipt.web.css";
import type { ComponentProps } from "./props";
import { useCapabilityExposure } from "../../hooks/useCapabilityExposure";

export const SportsbookReceipt: FunctionComponent<ComponentProps> = ({
  config,
  receiptTopContentPlacement,
  upsellReceiptSuggestions,
  selections,
  marketSelections,
  boostedMultiples,
  multiples,
  singles,
  oneLineBets,
  casts,
  betBuilders,
  multiBetBuilder,
  multiBetBuilderGroups,
  i18n,
  potentialReturns,
  totalOriginalReturns,
  totalStake,
  isOddsBoosted,
  hasShownReceiptIds,
  hasBoostSignposting,
  slots,
  isTrapIconEnabled,
  dispatchAccordionToggle,
  dispatchReUseSelections,
  dispatchCopyBetIdAction,
  dispatchCopyRegulatorBetIdAction,
}) => {
  const { isDesktopLayout } = config;
  const isBottomPlacement = receiptTopContentPlacement.enabled && receiptTopContentPlacement.placement === "BOTTOM";

  useCapabilityExposure(receiptTopContentPlacement.enabled ? receiptTopContentPlacement.exposure : null);
  useCapabilityExposure(upsellReceiptSuggestions.exposure);

  const bottomContent = useMemo(() => {
    const clubTracker = isBottomPlacement ? slots.receiptTopContent : null;
    const upsell = upsellReceiptSuggestions.enabled ? (
      <UpsellSuggestions location="receipt" marketSelections={marketSelections} isDesktop={isDesktopLayout} />
    ) : null;

    if (!upsell && !clubTracker) {
      return null;
    }

    return (
      <>
        {upsell}
        {clubTracker}
      </>
    );
  }, [isBottomPlacement, slots.receiptTopContent, upsellReceiptSuggestions.enabled, marketSelections, isDesktopLayout]);

  const hasOnlyOneLineBets = useMemo(() => singles.length === 0 && !!oneLineBets?.length, [oneLineBets, singles]);

  const handleAccordionToggle = useCallback(
    (isExpanded: boolean) => {
      dispatchAccordionToggle?.(isExpanded);
    },
    [dispatchAccordionToggle],
  );
  const handleReUseSelectionsClick = useCallback(() => {
    dispatchReUseSelections(selections);
  }, [dispatchReUseSelections, selections]);

  const onBetIdCopy = useCallback(() => {
    dispatchCopyBetIdAction();
  }, [dispatchCopyBetIdAction]);

  const onRegulatorBetIdCopy = useCallback(() => {
    dispatchCopyRegulatorBetIdAction();
  }, [dispatchCopyRegulatorBetIdAction]);

  const betslipReceiptClass = classNames(styles.scrollableReceipt, {
    [styles.scrollableReceiptDesktop]: isDesktopLayout,
    [styles.scrollableReceiptModal]: !isDesktopLayout,
  });

  return (
    <div className={betslipReceiptClass}>
      <BetslipSportsbookReceiptPanel
        selections={selections}
        multiples={multiples}
        boostedMultiples={boostedMultiples}
        singles={singles}
        oneLineBets={oneLineBets}
        casts={casts}
        betBuilders={betBuilders}
        multiBetBuilder={multiBetBuilder}
        multiBetBuilderGroups={multiBetBuilderGroups}
        i18n={i18n}
        potentialReturns={potentialReturns}
        totalOriginalReturns={totalOriginalReturns}
        totalStake={totalStake}
        isOddsBoosted={isOddsBoosted}
        onTitleClick={handleAccordionToggle}
        onReUseSelectionsClick={handleReUseSelectionsClick}
        showReuseSelectionsButton={!hasOnlyOneLineBets}
        onBetIdCopy={onBetIdCopy}
        onRegulatorBetIdCopy={onRegulatorBetIdCopy}
        hasBoostSignposting={hasBoostSignposting}
        hasShownReceiptIds={hasShownReceiptIds}
        isDesktop={isDesktopLayout}
        topContent={isBottomPlacement ? null : slots.receiptTopContent}
        bottomContent={bottomContent}
        isTrapIconEnabled={isTrapIconEnabled}
      />
    </div>
  );
};
